import React, { useMemo } from 'react';
import TickEntrega from '../TickEntrega';
import { AVISO_VENTANA_CERRADA } from '../../utils/ventanaWhatsApp';
import MediaBubble from './MediaBubble';
import AdjuntarArchivo from './AdjuntarArchivo';
import { ordenarMensajes } from '../../utils/ordenMensajes';

const ZONA_POR_DEFECTO = 'Europe/Madrid';
const OPCIONES_FECHA = {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
};

// Un Intl.DateTimeFormat por zona: crearlo es lo caro de formatear, y antes se
// creaba uno por mensaje y por render con toLocaleString. Una zona que el
// navegador no conoce cae a la hora local, como hacía el catch de antes.
const formateadores = new Map();
export function formateadorDeZona(zona) {
  let f = formateadores.get(zona);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat('es-ES', { ...OPCIONES_FECHA, timeZone: zona });
    } catch (e) {
      f = new Intl.DateTimeFormat('es-ES', OPCIONES_FECHA);
    }
    formateadores.set(zona, f);
  }
  return f;
}

function textoHora(msg, botTz) {
  // Manejar diferentes formatos de fecha
  let timeToUse = msg.timestamp || msg.createdAt;

  // Si no hay timestamp/createdAt, usar la fecha actual con la hora del mensaje
  if (!timeToUse && msg.time) {
    const today = new Date();
    const timeParts = msg.time.split(':');
    if (timeParts.length >= 2) {
      today.setHours(parseInt(timeParts[0]) || 0);
      today.setMinutes(parseInt(timeParts[1]) || 0);
      today.setSeconds(parseInt(timeParts[2]) || 0);
      timeToUse = today.toISOString();
    }
  }

  // Los timestamps del backend (LocalDateTime) llegan sin marca de zona,
  // pero están en UTC. Forzar parseo como UTC añadiendo 'Z' si falta.
  if (typeof timeToUse === 'string' &&
      !/[zZ]|[+-]\d{2}:?\d{2}$/.test(timeToUse)) {
    timeToUse = timeToUse + 'Z';
  }

  const date = new Date(timeToUse);
  const conUsuario = (texto) => (
    // Si el mensaje fue enviado por un usuario desde la plataforma, mostrar username
    msg.sender === 'agent' && msg.senderUsername ? `${texto} - ${msg.senderUsername}` : texto
  );

  // Si la fecha es inválida, mostrar solo la hora
  if (isNaN(date.getTime())) {
    return conUsuario(msg.time || 'Sin fecha');
  }
  return conUsuario(formateadorDeZona(botTz).format(date));
}

function parsearBotones(raw) {
  if (!raw) return null;
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch (e) {
    console.error('Error parseando botones:', e);
    return null;
  }
}

// Una fila por mensaje. Va con memo: escribir en el input, un tick o un evento de
// archivo de OTRO mensaje vuelven a pintar el panel, pero la fila solo se repinta
// si cambia su propio mensaje (objeto nuevo), la zona o el callback de carga.
const FilaMensaje = React.memo(function FilaMensaje({ msg, botTz, alCargarMedia }) {
  const buttons = parsearBotones(msg.buttons);
  return (
    <div className={`message ${msg.sender}`}>
      <div className="message-content">
        <p>{msg.text}</p>
        <MediaBubble msg={msg} onCargado={alCargarMedia} />

        {/* Mostrar botones si existen (solo visuales, sin funcionalidad) */}
        {buttons && Array.isArray(buttons) && buttons.length > 0 && (
          <div className="template-buttons">
            {buttons.map((button, index) => {
              if (button.type === 'QUICK_REPLY') {
                return (
                  <button
                    key={index}
                    className="template-button quick-reply"
                    disabled
                    style={{ cursor: 'default' }}
                  >
                    {button.text}
                  </button>
                );
              } else if (button.type === 'URL') {
                return (
                  <span
                    key={index}
                    className="template-button url-button"
                    style={{ cursor: 'default' }}
                  >
                    {button.text}
                  </span>
                );
              } else if (button.type === 'PHONE_NUMBER') {
                return (
                  <span
                    key={index}
                    className="template-button phone-button"
                    style={{ cursor: 'default' }}
                  >
                    {button.text}
                  </span>
                );
              }
              return null;
            })}
          </div>
        )}

        <span className="message-time">
          {textoHora(msg, botTz)}
          {msg.sender === 'agent' && (
            <TickEntrega
              estado={msg.deliveryStatus}
              errorCode={msg.deliveryErrorCode}
              errorMessage={msg.deliveryErrorMessage}
            />
          )}
        </span>
      </div>
    </div>
  );
});

// Panel de la conversación abierta: cabecera del chat (con acciones de atención),
// la lista de mensajes con su paginación hacia atrás y el input de envío. Cuando no
// hay chat seleccionado pinta el placeholder "Selecciona una conversación". La
// lógica (estado, carga, envío, scroll) vive en el hook useMessages; aquí solo se
// pinta lo que recibe por props desde el Dashboard.
function MessagesPanel({
  selectedChat,
  selectedBotId,
  accessibleBots,
  supportStatus,
  supportActionLoading,
  handleCaptureConversation,
  handleCloseConversation,
  messagesContainerRef,
  messagesPagination,
  loadMoreMessages,
  message,
  setMessage,
  handleKeyPress,
  handleSendMessage,
  messageInputRef,
  ventanaCerrada = false,
  sendErrorText = null,
  onVerFicha = null,
  alCargarMedia = undefined,
  enviandoMedia = false,
  handleSendMedia = undefined,
}) {
  const aviso = ventanaCerrada ? AVISO_VENTANA_CERRADA : sendErrorText;

  const mensajes = selectedChat?.messages;
  const mensajesOrdenados = useMemo(() => ordenarMensajes(mensajes), [mensajes]);

  // Zona horaria del bot del chat: una vez por render, no una por mensaje.
  const chatBotId = selectedChat?.botId ?? selectedBotId;
  const botTz = useMemo(() => {
    const chatBot = chatBotId != null
      ? (accessibleBots || []).find(b => Number(b.botId) === Number(chatBotId))
      : null;
    return chatBot?.timezone || ZONA_POR_DEFECTO;
  }, [accessibleBots, chatBotId]);

  const puedeVerFicha = !!(
    onVerFicha
    && selectedChat?.internalChatId
    && (selectedChat.platform === 'whatsapp' || selectedChat.platform === 'instagram')
  );

  return (
    selectedChat ? (
      <>
        {/* Chat Header */}
        <div className="chat-header">
          <div className="chat-header-info">
            <button className="back-to-chats-btn" onClick={() => {
              // Usar history.back() para que sea consistente con el botón de atrás del móvil
              window.history.back();
            }}>
              <svg className="back-arrow-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="back-text">Volver</span>
            </button>
            <div className={`chat-avatar platform-badge ${selectedChat.platform}`}>
              <img 
                src={selectedChat.platform === 'telegram' ? '/tg.png' : selectedChat.platform === 'instagram' ? '/ig.png' : '/wa.svg'} 
                alt={selectedChat.platform}
                className="platform-icon"
              />
            </div>
            <div>
              <h3>{selectedChat.name}</h3>
            </div>
          </div>
          {puedeVerFicha || supportStatus ? (
            <div className="chat-header-actions">
              {puedeVerFicha ? (
                <button
                  type="button"
                  className="admin-btn secondary chat-vars-btn"
                  onClick={() => onVerFicha(selectedChat)}
                >
                  Ver ficha
                </button>
              ) : null}
              {/* Atención al cliente: capturar / cerrar desde el chat en vivo */}
              {supportStatus && (
                <div className="chat-header-support">
                  {supportStatus.captured ? (
                    <>
                      <span className="support-badge">
                        {supportStatus.mine
                          ? 'La estás atendiendo tú'
                          : `Atendida por ${supportStatus.agentUsername || 'otro agente'}`}
                      </span>
                      <button
                        className="chat-support-btn close"
                        onClick={handleCloseConversation}
                        disabled={supportActionLoading}
                        title="Cerrar la conversación de atención"
                      >
                        {supportActionLoading ? '…' : 'Cerrar conversación'}
                      </button>
                    </>
                  ) : supportStatus.canAttend ? (
                    <button
                      className="chat-support-btn capture"
                      onClick={handleCaptureConversation}
                      disabled={supportActionLoading}
                      title="Capturar la conversación y atenderla desde tu bandeja"
                    >
                      {supportActionLoading ? '…' : 'Capturar conversación'}
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Messages Container */}
        <div className="messages-container" ref={messagesContainerRef}>
          {/* Indicador de carga inicial: se muestra mientras llega la
              primera página, para que el panel no se vea vacío durante
              la animación de entrada. */}
          {messagesPagination.isLoading
            && messagesPagination.currentPage === 0
            && (selectedChat.messages?.length || 0) === 0 && (
            <div className="chat-messages-loading">
              <span className="chat-messages-spinner" />
            </div>
          )}

          {/* Indicador de carga de mensajes antiguos */}
          {messagesPagination.isLoading && messagesPagination.currentPage > 0 && (
            <div style={{ 
              padding: '10px', 
              textAlign: 'center', 
              color: '#666',
              fontSize: '0.9rem'
            }}>
              Cargando mensajes anteriores...
            </div>
          )}
          
          {/* Indicador de más mensajes disponibles */}
          {messagesPagination.hasMore && !messagesPagination.isLoading && (
            <div style={{ 
              padding: '10px', 
              textAlign: 'center', 
              color: '#000000',
              fontSize: '0.85rem',
              cursor: 'pointer'
            }} onClick={loadMoreMessages}>
              {messagesPagination.totalElements - (selectedChat.messages?.length || 0)} mensajes más antiguos
            </div>
          )}
          
          <div className="messages-list">
            {mensajesOrdenados.map((msg) => (
              <FilaMensaje key={msg.id} msg={msg} botTz={botTz} alCargarMedia={alCargarMedia} />
            ))}
          </div>
        </div>


        {/* Message Input */}
        <div className="message-input-container">
          {aviso ? (
            <div className="whatsapp-window-warning" role="alert">{aviso}</div>
          ) : null}
          <AdjuntarArchivo
            plataforma={selectedChat.platform}
            deshabilitado={ventanaCerrada}
            enviando={enviandoMedia}
            onEnviar={handleSendMedia}
            chatKey={selectedChat.internalChatId ?? selectedChat.chatId}
          >
            <textarea
              ref={messageInputRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              onFocus={(e) => {
                // Mantener el textarea visible encima del teclado cuando recibe el foco
                setTimeout(() => {
                  e.target.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'end',
                    inline: 'nearest'
                  });
                }, 100);
              }}
              placeholder="Escribe tu mensaje..."
              rows="1"
              disabled={ventanaCerrada}
            />
            <button 
              className={`send-btn ${message.trim() ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
              onMouseDown={(e) => {
                // Prevenir que el botón capture el foco del textarea
                e.preventDefault();
              }}
              disabled={ventanaCerrada || !message.trim()}
            >
              ➤
            </button>
          </AdjuntarArchivo>
        </div>
      </>
    ) : (
      <div className="no-chat-selected">
        <div className="no-chat-content">
          <h2>Selecciona una conversación</h2>
          <p>Elige un chat de la lista para comenzar a responder</p>
        </div>
      </div>
    )
  );
}

export default MessagesPanel;
